import React, { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  Divider,
  CircularProgress,
  LinearProgress,
  Paper,
  Snackbar,
  Tooltip,
  IconButton,
  Stack,
  FormControlLabel,
  Checkbox,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  TextField,
} from '@mui/material';
import {
  ArrowForward,
  ArrowBack,
  PlayArrow as RunIcon,
  Stop as StopIcon,
  CheckCircle,
  Refresh as RefreshIcon,
  PhotoFilter as AnalysisIcon,
  SkipNext as SkipIcon,
  SelectAll as SelectAllIcon,
  Deselect as DeselectIcon,
  SmartToy as ModelIcon,
  AccessTime as TimeIcon,
  AttachMoney as CostIcon,
} from '@mui/icons-material';
import imageService from '../../api/imageService';
import sessionService from '../../api/sessionService';
import AuthenticatedImage from '../AuthenticatedImage';
import SessionEventTypeManager from '../SessionEventTypeManager';
import { getServerUrl } from '../../config/runtimeConfig';

const API_URL = getServerUrl();

/**
 * WizardStep3Analysis
 * Props:
 *   session   – current session object
 *   onNext    – advance wizard
 *   onBack    – go back
 */
const WizardStep3Analysis = ({ session, onNext, onBack, onRunComplete, onNavConfigChange }) => {
  const sessionId = session?.uniqueId;

  // ── Local session state (refreshed after event type changes) ──────────────
  const [currentSession, setCurrentSession] = useState(session);

  useEffect(() => {
    setCurrentSession(session);
  }, [session]);

  const refreshCurrentSession = useCallback(async () => {
    try {
      const updated = await sessionService.getSession(sessionId);
      setCurrentSession(updated);
    } catch (err) {
      console.warn('[Step3] Failed to refresh session', err);
    }
  }, [sessionId]);

  // ── Existing scene data stats ──────────────────────────────────────────────
  const [embeddingStats, setEmbeddingStats] = useState(null); // { totalImages, embeddedImages, notEmbedded }
  const [loadingStats, setLoadingStats] = useState(false);

  // ── Subset image selection ─────────────────────────────────────────────────
  const [allImages, setAllImages] = useState([]);
  const [selPage, setSelPage] = useState(0);
  const [selRowsPerPage, setSelRowsPerPage] = useState(12);
  const [selTotal, setSelTotal] = useState(0);
  const [selLoading, setSelLoading] = useState(false);
  const [selectedImageIds, setSelectedImageIds] = useState([]);
  const [useSubset, setUseSubset] = useState(false);

  // ── Analysis run state ────────────────────────────────────────────────────
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  // { status, current, total, currentImageId, currentImageName, message }
  const [completed, setCompleted] = useState(false);
  const [stuckOperation, setStuckOperation] = useState(false); // 409 — previous run still in-progress in DB
  const [lastRunResult, setLastRunResult] = useState(null); // result from the most recent run
  const [runOptions, setRunOptions] = useState({ forceRegenerate: false, imageQuality: 'high', temperature: 0.3 });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    onNavConfigChange?.({
      nextLabel: 'Continue',
      nextDisabled: false,
      backDisabled: running,
      showSkip: true,
      skipLabel: 'Skip',
    });
  }, [running]); // onNavConfigChange is stable

  // ── Socket.IO ref ──────────────────────────────────────────────────────────────────────────────
  const socketRef = useRef(null);
  // Prevents stale in-flight progress events from overwriting a cancelled/completed state
  const isFinishedRef = useRef(false);
  // Snapshot of progress.current at cancel-click time. Non-null = freeze progress events.
  const cancelledAtCurrentRef = useRef(null);

  // ── Snackbar ──────────────────────────────────────────────────────────────
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const showSnackbar = (message, severity = 'info') =>
    setSnackbar({ open: true, message, severity });

  // ─────────────────────────────────────────────────────────────────────────
  // Load existing scene stats
  // ─────────────────────────────────────────────────────────────────────────
  const loadEmbeddingStats = useCallback(async () => {
    if (!sessionId) return;
    setLoadingStats(true);
    try {
      const data = await imageService.getEmbeddingStats(sessionId);
      setEmbeddingStats(data.stats);
    } catch (err) {
      console.warn('[Step3] Could not load embedding stats', err);
    } finally {
      setLoadingStats(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadEmbeddingStats();
  }, [loadEmbeddingStats]);

  // ─────────────────────────────────────────────────────────────────────────
  // Load images for subset selection panel
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!useSubset || !sessionId) return;
    const load = async () => {
      setSelLoading(true);
      try {
        const resp = await sessionService.getSessionImages(sessionId, {
          page: selPage + 1,
          limit: selRowsPerPage,
          sortBy: 'createdAt',
          sortOrder: 'desc',
          mediaType: 'image',
        });
        setAllImages(resp.images || []);
        setSelTotal(resp.pagination?.totalCount || resp.pagination?.total || 0);
      } catch (err) {
        console.error('[Step3] Failed to load images for selection', err);
      } finally {
        setSelLoading(false);
      }
    };
    load();
  }, [sessionId, useSubset, selPage, selRowsPerPage]);

  // ─────────────────────────────────────────────────────────────────────────
  // Socket.IO — subscribe/unsubscribe
  // ─────────────────────────────────────────────────────────────────────────
  const connectSocket = useCallback(() => {
    if (socketRef.current) return; // already connected

    const socket = io(API_URL, {
      path: '/socket.io/',
      withCredentials: true,
      transports: ['polling'],
    });

    socket.on('connect', () => {
      console.log('[Step3] Socket.IO connected:', socket.id);
      socket.emit('joinEmbeddingRoom', { sessionId });
    });

    socket.on('embeddingProgress', (data) => {
      if (data.sessionId !== sessionId) return;
      if (isFinishedRef.current) return; // ignore stale events after cancel/complete
      if (cancelledAtCurrentRef.current !== null) return; // freeze after cancel-click; wait for complete event
      setRunning(true); // ensure running=true even if session prop was stale when tab opened
      const fname = data.filename || data.imageName || '';
      const statusMsg = data.status === 'skipped'
        ? `Skipped: ${fname}`
        : data.status === 'success'
        ? `Analysed: ${fname}`
        : data.status === 'error'
        ? `Error: ${fname}`
        : fname ? `Processing: ${fname}` : 'Processing\u2026';
      setProgress({
        status: data.status || 'processing',
        current: data.current ?? data.progress ?? 0,
        total: data.total ?? 0,
        currentImageId: data.imageId || data.currentImageId || null,
        currentImageName: fname,
        message: statusMsg,
        lastProcessingTimeMs: data.lastProcessingTimeMs || 0,
        avgProcessingTimeMs: data.avgProcessingTimeMs || 0,
        estimatedTimeRemainingMs: data.estimatedTimeRemainingMs || 0,
        estimatedTimeRemainingFormatted: data.estimatedTimeRemainingFormatted || null,
        runningCostUsd: data.runningCostUsd ?? null,
      });
    });

    socket.on('embeddingComplete', (data) => {
      if (data.sessionId !== sessionId) return;
      isFinishedRef.current = true;
      setRunning(false);
      // data.status comes from backend (added to completePayload); data.result.cancelled is the fallback
      const wasCancelled = data.status === 'cancelled' || data.result?.cancelled || false;
      setCompleted(!wasCancelled);
      if (!wasCancelled && data.result) setLastRunResult(data.result);
      setProgress((prev) => {
        // For current: own tab uses cancel-click snapshot; other tab uses backend's actual processed count
        const backendProcessed = (data.result?.processed ?? 0) + (data.result?.skipped ?? 0);
        const cancelledCurrent = cancelledAtCurrentRef.current ?? (backendProcessed || null);
        return {
          ...(prev || {}),
          status: wasCancelled ? 'cancelled' : 'completed',
          current: wasCancelled ? (cancelledCurrent ?? prev?.current ?? 0) : (prev?.total ?? 0),
          // Preserve total so progressPct = current/total gives correct cancelled %
          total: prev?.total ?? data.result?.total ?? 0,
          message: wasCancelled ? 'Generation cancelled.' : 'Embedding generation complete!',
        };
      });
      if (!wasCancelled) loadEmbeddingStats();
      if (!wasCancelled && onRunComplete) onRunComplete();
      showSnackbar(
        wasCancelled
          ? 'Embedding generation cancelled.'
          : data.status === 'failed'
          ? `Generation failed: ${data.error}`
          : (() => {
              const processed = data.result?.processed || 0;
              const skipped = data.result?.skipped || 0;
              const total = processed + skipped;
              return skipped > 0
                ? `Embedding generation complete — ${total} image(s) analysed (${processed} new, ${skipped} already embedded).`
                : `Embedding generation complete — ${processed} image(s) embedded.`;
            })(),
        wasCancelled ? 'warning' : data.status === 'failed' ? 'error' : 'success'
      );
    });

    socket.on('disconnect', () => {
      console.log('[Step3] Socket.IO disconnected');
    });

    socketRef.current = socket;
  }, [sessionId, loadEmbeddingStats]);

  const disconnectSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  // Detect a run already in-progress when the component mounts (e.g. page refresh).
  // Sets running=true so the Run button is disabled and the progress bar is shown.
  // The socket (connected just above) will receive future events and fill in progress.
  useEffect(() => {
    if (session?.stepStatus?.embeddings?.inProgress) {
      setRunning(true);
      setProgress((prev) => prev ?? {
        status: 'processing',
        current: session.stepStatus.embeddings.current ?? 0,
        total:   session.stepStatus.embeddings.total   ?? 0,
        message: 'Embedding generation in progress — reconnected…',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount only

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connectSocket();
    return () => disconnectSocket();
  }, [connectSocket, disconnectSocket]);

  // ─────────────────────────────────────────────────────────────────────────
  // Embedding generation
  // ─────────────────────────────────────────────────────────────────────────
  const handleRunAnalysis = async () => {
    if (!sessionId || running) return;

    setStuckOperation(false);
    setRunning(true);
    setCompleted(false);
    isFinishedRef.current = false;
    cancelledAtCurrentRef.current = null;
    setProgress({ status: 'starting', current: 0, total: 0, message: 'Starting embedding generation…' });

    const targetIds = useSubset && selectedImageIds.length > 0 ? selectedImageIds : [];

    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(
        `${API_URL}/api/embeddings/generate/session/${sessionId}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            imageIds: targetIds.length > 0 ? targetIds : undefined,
            socketId: socketRef.current?.id,
            forceRegenerate: runOptions.forceRegenerate,
            imageQuality: runOptions.imageQuality,
            temperature: runOptions.temperature,
            // Pass user-defined event types (may be multiple) — distinct from AI-detected image eventType
            eventTypes: currentSession?.settings?.eventTypes?.length > 0
              ? currentSession.settings.eventTypes
              : undefined,
          }),
        }
      );

      if (!resp.ok) {
        if (resp.status === 409) {
          setRunning(false);
          setProgress(null);
          setStuckOperation(true);
          return;
        }
        const errData = await resp.json().catch(() => ({ message: resp.statusText }));
        throw new Error(errData.message || 'Failed to start embedding generation');
      }

      const result = await resp.json();

      if (result.status === 'skipped') {
        setRunning(false);
        setCompleted(true);
        setProgress({ status: 'completed', current: 0, total: 0, message: result.message });
        loadEmbeddingStats();
        showSnackbar(result.message, 'info');
        return;
      }

      // Guard: do not overwrite progress if the embeddingComplete socket event has
      // already arrived and marked the run as completed (race between HTTP response
      // and WebSocket event since the backend fires both simultaneously).
      setProgress((prev) => {
        if (prev?.status === 'completed' || prev?.status === 'cancelled') return prev;
        const total = result.result?.total ?? prev?.total ?? 0;
        return {
          status: 'processing',
          current: prev?.current ?? 0,
          total,
          message: `Preparing ${total} image(s)…`,
        };
      });
    } catch (err) {
      setRunning(false);
      setProgress(null);
      showSnackbar('Failed to start embedding generation: ' + err.message, 'error');
    }
  };

  const handleCancelAnalysis = async () => {
    // Snapshot current counter before stale in-flight events can advance it
    cancelledAtCurrentRef.current = progress?.current ?? 0;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/embeddings/cancel/${sessionId}`, {
        method: 'POST',
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.warn('[Step3] Cancel request failed', err);
    }
    setRunning(false);
    setProgress((prev) => ({ ...(prev || {}), status: 'cancelled', message: 'Generation cancelled.' }));
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Subset helpers
  // ─────────────────────────────────────────────────────────────────────────
  const toggleImage = (id) =>
    setSelectedImageIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const selectPage = () => {
    const ids = allImages.map((img) => img.uniqueId);
    setSelectedImageIds((prev) => [...new Set([...prev, ...ids])]);
  };

  const deselectPage = () => {
    const pageIds = new Set(allImages.map((img) => img.uniqueId));
    setSelectedImageIds((prev) => prev.filter((id) => !pageIds.has(id)));
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Derived
  // ─────────────────────────────────────────────────────────────────────────
  const embeddedCount = embeddingStats?.totalEmbeddings ?? embeddingStats?.embeddedImages ?? embeddingStats?.count ?? 0;

  // Format milliseconds into a readable string
  const formatTime = (ms) => {
    if (!ms || ms <= 0) return null;
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    const totalSec = Math.floor(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const totalCount = embeddingStats?.totalImages ?? 0;
  const notEmbedded = totalCount - embeddedCount;
  const allEmbedded = totalCount > 0 && notEmbedded <= 0;

  const progressPct = progress
    ? progress.total > 0
      ? Math.round((progress.current / progress.total) * 100)
      : progress.status === 'completed' ? 100 : 0
    : 0;

  const canContinue = true; // can always skip

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* ── Top navigation ──────────────────────────────────────────────────────── */}
      {/* ── Instructions ── */}
      <Box sx={{ px: 2, py: 1.5, mb: 2.5, bgcolor: 'grey.50', borderLeft: '3px solid', borderColor: 'primary.main', borderRadius: '0 8px 8px 0' }}>
        <Typography variant="body2" color="text.secondary">
          Each photo is analysed by AI (OpenAI Vision) to extract visual features and generate
          embedding vectors. These vectors are required for clustering in Step 4. You can skip
          this step, but clustering quality will be lower.
        </Typography>
      </Box>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          AI Image Analysis & Embeddings
        </Typography>
        <Tooltip title="Refresh embedding stats">
          <IconButton size="small" onClick={loadEmbeddingStats} disabled={loadingStats}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Configuration section header ───────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, mt: 0.5 }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, lineHeight: 1, whiteSpace: 'nowrap' }}>Configuration</Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
      </Box>

      {/* ── Stats bar ───────────────────────────────────────────────────────── */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          bgcolor: 'action.hover',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AnalysisIcon fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary">
            Embedding stats:
          </Typography>
        </Box>
        {loadingStats ? (
          <CircularProgress size={16} />
        ) : (
          <>
            <Chip size="small" label={`${totalCount} total images`} variant="outlined" />
            <Chip
              size="small"
              label={`${embeddedCount} embedded`}
              color={allEmbedded ? 'success' : 'primary'}
              variant={embeddedCount > 0 ? 'filled' : 'outlined'}
            />
            {notEmbedded > 0 && !allEmbedded && (
              <Chip
                size="small"
                label={`${notEmbedded} not yet embedded`}
                color="warning"
                variant="filled"
              />
            )}
            {embeddingStats?.visionModel && (
              <Tooltip title="Vision model used for image analysis">
                <Chip
                  size="small"
                  icon={<ModelIcon />}
                  label={embeddingStats.visionModel}
                  variant="outlined"
                />
              </Tooltip>
            )}
            {embeddingStats?.embeddingModel && (
              <Tooltip title="Embedding model used for vector generation">
                <Chip
                  size="small"
                  label={embeddingStats.embeddingModel}
                  variant="outlined"
                />
              </Tooltip>
            )}
            {embeddingStats?.lastAnalyzedAt && (
              <Tooltip title={`Last embedding run: ${new Date(embeddingStats.lastAnalyzedAt).toLocaleString('en-GB')}`}>
                <Chip
                  size="small"
                  icon={<TimeIcon />}
                  label={new Date(embeddingStats.lastAnalyzedAt).toLocaleDateString('en-GB', {
                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                  variant="outlined"
                />
              </Tooltip>
            )}
            {embeddingStats?.costEstimateUSD > 0 && (
              <Tooltip title={
                embeddingStats.costIsEstimated === false && embeddingStats.tokenStats
                  ? `Actual cost: ${embeddingStats.tokenStats.visionTotalTokens?.toLocaleString()} vision tokens + ${embeddingStats.tokenStats.embeddingTokens?.toLocaleString()} embedding tokens`
                  : 'Estimated API cost (approximate — based on ~$0.003 per image)'
              }>
                <Chip
                  size="small"
                  icon={<CostIcon />}
                  label={`${embeddingStats.costIsEstimated === false ? '' : '~'}$${embeddingStats.costEstimateUSD.toFixed(4)}`}
                  variant="outlined"
                  color={embeddingStats.costIsEstimated === false ? 'secondary' : 'default'}
                />
              </Tooltip>
            )}
            {embeddingStats?.tokenStats && (
              <Tooltip title={`${embeddingStats.tokenStats.totalTokens?.toLocaleString()} total tokens (${embeddingStats.tokenStats.visionTotalTokens?.toLocaleString()} vision + ${embeddingStats.tokenStats.embeddingTokens?.toLocaleString()} embedding)`}>
                <Chip
                  size="small"
                  label={`${embeddingStats.tokenStats.totalTokens?.toLocaleString()} tokens`}
                  variant="outlined"
                  color="info"
                />
              </Tooltip>
            )}
          </>
        )}
      </Paper>

      {/* ── Event Type ────────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        {!(currentSession?.settings?.eventTypes?.length > 0 || currentSession?.settings?.eventType) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No event type set for this session. Setting one helps the AI tailor questions
            12 and 14 (album worthiness check) to your specific event.
          </Alert>
        )}
        <SessionEventTypeManager
          currentSession={currentSession}
          refreshCurrentSession={refreshCurrentSession}
          showSnackbar={showSnackbar}
        />
      </Paper>

      {/* ── Options ─────────────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Options
        </Typography>
        <Stack spacing={0.5}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={runOptions.forceRegenerate}
                onChange={(e) => setRunOptions((o) => ({ ...o, forceRegenerate: e.target.checked }))}
                disabled={running}
              />
            }
            label={<Typography variant="body2">Regenerate embeddings for already-processed images</Typography>}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={useSubset}
                onChange={(e) => {
                  setUseSubset(e.target.checked);
                  if (!e.target.checked) setSelectedImageIds([]);
                }}
                disabled={running}
              />
            }
            label={<Typography variant="body2">Run on selected images only</Typography>}
          />
          <Box sx={{ mt: 1 }}>
            <TextField
              label="Vision temperature"
              type="number"
              size="small"
              value={runOptions.temperature}
              onChange={(e) => {
                const v = Math.max(0, Math.min(1, parseFloat(e.target.value) || 0));
                setRunOptions((o) => ({ ...o, temperature: v }));
              }}
              sx={{ width: 160 }}
              inputProps={{ min: 0, max: 1, step: 0.05 }}
              helperText="Creativity of image descriptions (default: 0.3)"
              disabled={running}
            />
          </Box>
        </Stack>
      </Paper>

      {/* ── Subset image selection panel ─────────────────────────────────────── */}
      {useSubset && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
              Select Images ({selectedImageIds.length} selected)
            </Typography>
            <Button size="small" startIcon={<SelectAllIcon />} onClick={selectPage} disabled={running}>
              Select page
            </Button>
            <Button size="small" startIcon={<DeselectIcon />} onClick={deselectPage} disabled={running}>
              Deselect page
            </Button>
            <Button size="small" color="warning" onClick={() => setSelectedImageIds([])} disabled={running || selectedImageIds.length === 0}>
              Clear all
            </Button>
          </Box>

          {selLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <Grid container spacing={1.5}>
                {allImages.map((img) => {
                  const selected = selectedImageIds.includes(img.uniqueId);
                  return (
                    <Grid item xs={6} sm={4} md={3} lg={2} key={img.uniqueId}>
                      <Card
                        onClick={() => !running && toggleImage(img.uniqueId)}
                        sx={{
                          cursor: running ? 'default' : 'pointer',
                          border: selected ? '2px solid' : '2px solid transparent',
                          borderColor: selected ? 'primary.main' : 'transparent',
                          position: 'relative',
                        }}
                      >
                        <AuthenticatedImage
                          imageId={img.uniqueId}
                          type="thumbnail"
                          fallbackToServe
                          alt={img.name}
                          sx={{ width: '100%', height: 100, objectFit: 'contain', display: 'block', bgcolor: 'grey.50' }}
                        />
                        {selected && (
                          <Box
                            sx={{
                              position: 'absolute', top: 4, right: 4,
                              bgcolor: 'primary.main', borderRadius: '50%',
                              width: 20, height: 20,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >
                            <CheckCircle sx={{ color: 'white', fontSize: 16 }} />
                          </Box>
                        )}
                        <CardContent sx={{ py: 0.5, px: 1 }}>
                          <Typography variant="caption" noWrap display="block">{img.name}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              <TablePagination
                component="div"
                count={selTotal}
                page={selPage}
                onPageChange={(_, p) => setSelPage(p)}
                rowsPerPage={selRowsPerPage}
                onRowsPerPageChange={(e) => { setSelRowsPerPage(parseInt(e.target.value, 10)); setSelPage(0); }}
                rowsPerPageOptions={[12, 24, 48]}
                sx={{ mt: 0.5 }}
              />
            </>
          )}
        </Paper>
      )}

      {/* ── Stuck-operation recovery ─────────────────────────────────────────── */}
      {stuckOperation && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="warning"
              size="small"
              variant="outlined"
              onClick={async () => {
                try {
                  await imageService.forceResetEmbeddings(sessionId);
                  setStuckOperation(false);
                  handleRunAnalysis();
                } catch (err) {
                  showSnackbar('Failed to clear stuck operation: ' + err.message, 'error');
                }
              }}
            >
              Force Reset &amp; Retry
            </Button>
          }
        >
          A previous embedding run is still marked as <strong>in progress</strong> in the database
          (the backend may have restarted mid-run). Click{' '}
          <strong>Force Reset &amp; Retry</strong> to clear it and start a new run.
        </Alert>
      )}

      {/* ── Early-exit tip when all done ─────────────────────────────────────── */}
      {allEmbedded && !running && !progress && (
        <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
          All images in this session already have embeddings. Enable{' '}
          <strong>&ldquo;Regenerate&rdquo;</strong> above to re-run, or continue to the next step.
        </Alert>
      )}

      {/* ── Run / Cancel buttons ─────────────────────────────────────────────── */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={running ? <CircularProgress size={18} color="inherit" /> : <RunIcon />}
          onClick={handleRunAnalysis}
          disabled={running || (useSubset && selectedImageIds.length === 0)}
        >
          {running
            ? 'Generating…'
            : useSubset && selectedImageIds.length > 0
            ? `Generate for ${selectedImageIds.length} selected photo(s)`
            : 'Generate Embeddings'}
        </Button>
        {running && (
          <Button
            variant="outlined"
            size="large"
            color="error"
            startIcon={<StopIcon />}
            onClick={handleCancelAnalysis}
          >
            Cancel
          </Button>
        )}
      </Stack>

      {/* ── Progress ─────────────────────────────────────────────────────────── */}
      {progress && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            {/* Thumbnail of the image currently being processed */}
            {progress.currentImageId && (
              <Box sx={{ flexShrink: 0 }}>
                <AuthenticatedImage
                  imageId={progress.currentImageId}
                  type="thumbnail"
                  fallbackToServe
                  alt={progress.currentImageName}
                  sx={{
                    width: 80,
                    height: 80,
                    objectFit: 'contain',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'grey.100',
                    display: 'block',
                  }}
                />
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '85%' }}>
                  {progress.message || 'Working…'}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {progressPct}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progressPct}
                color={
                  progress.status === 'completed'
                    ? 'success'
                    : progress.status === 'cancelled'
                    ? 'warning'
                    : 'primary'
                }
                sx={{ mb: 1 }}
              />
              {progress.total > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {progress.current} / {progress.total} photos
                  {progress.currentImageName ? ` — ${progress.currentImageName}` : ''}
                </Typography>
              )}
              {/* ── Timing metrics ─────────────────────────────────────────── */}
              {(progress.lastProcessingTimeMs > 0 || progress.avgProcessingTimeMs > 0 || progress.estimatedTimeRemainingFormatted) && (
                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {progress.lastProcessingTimeMs > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Last image: <strong>{formatTime(progress.lastProcessingTimeMs)}</strong>
                    </Typography>
                  )}
                  {progress.avgProcessingTimeMs > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Avg/image: <strong>{formatTime(progress.avgProcessingTimeMs)}</strong>
                    </Typography>
                  )}
                  {progress.estimatedTimeRemainingFormatted && progress.status !== 'completed' && progress.status !== 'cancelled' && (
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                      ETA: {progress.estimatedTimeRemainingFormatted}
                    </Typography>
                  )}
                  {progress.runningCostUsd > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Cost so far: <strong>${progress.runningCostUsd.toFixed(4)}</strong>
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {completed && (
            <Alert severity="success" icon={<CheckCircle />} sx={{ mt: 1.5 }}>
              {(() => {
                const processed = lastRunResult?.processed || 0;
                const skipped = lastRunResult?.skipped || 0;
                const total = processed + skipped;
                const detail = total > 0
                  ? skipped > 0
                    ? `${total} image(s) analysed (${processed} new, ${skipped} already embedded)`
                    : `${processed} image(s) embedded`
                  : `${progress?.total ?? 0} image(s) embedded`;
                return `Embedding generation complete \u2014 ${detail}. Continue to the next step.`;
              })()}
            </Alert>
          )}
          {progress.status === 'cancelled' && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              Generation was cancelled. You can re-run at any time.
            </Alert>
          )}
        </Paper>
      )}



      {/* ── Divider + Navigation ──────────────────────────────────────────────── */}
      <Divider sx={{ mt: 4, mb: 3 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          {completed || allEmbedded ? (
            <Alert severity="success" icon={<CheckCircle />} sx={{ py: 0.5 }}>
              {embeddedCount} image(s) embedded — proceed to clustering.
            </Alert>
          ) : (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Generate embeddings or skip to continue without AI analysis.
            </Alert>
          )}
        </Box>

        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={onBack} disabled={running}>
            Back
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<SkipIcon />}
            onClick={() => onNext({})}
            disabled={running}
          >
            Skip
          </Button>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForward />}
            onClick={() => onNext({ embeddedCount })}
            disabled={running}
            sx={{ minWidth: 180 }}
          >
            Continue
          </Button>
        </Stack>
      </Box>

      {/* ── Snackbar ─────────────────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WizardStep3Analysis;
